import BaseEvent from "./base_event";
import { PlayerAhoyCommonEvent } from "../player_ahoy_common_event";
import PlayerPlayInterval from '../player_play_interval';

export default class VideoEvent {
  constructor(options = {}){
    this._tech = options.tech;
    this._data = options.data;
    this._uuid = options.uuid;
    this._error = options.error;
    this._autoplay = options.autoplay;
    this._ads = options.ads;
    this._base = new BaseEvent(options);
    this._playerPlayInterval = new PlayerPlayInterval(this.tech(), this.data(), this.uuid(), options.autoplay, options.ads);
    this._playlistCheckerInterval = 1000;
    this._playlistChecker = undefined;
  }

  bindEvents(){
    this.base().bindEvents();
    this.getPlaylistStatus();
    this.bindPause();
    this.bindResume();
    this.bindSeek();
    this.bindMeta();
    this.bindBuffer();
    this.bindError();
    this.bindStart();
    this.bindPlay();

    this.bindAdError();
    this.bindAdWatch();

    this.bindUnmuteButtonLoad();
    this.bindUnmuteButtonClick();

    this.bindTokenRefresh();
    this.bindDownloadAppButtonClick();
    this.bindHrefButtonClick();
    this.bindFeedbackButtonClick();
    this.bindReportButtonClick();
    this.bindRetryButtonClick();
  }

  base(){
    return this._base;
  }

  data(){
    return this._data;
  }

  autoplay(){
    return this._autoplay;
  }

  ads(){
    return this._ads;
  }

  uuid(){
    return this._uuid;
  }

  tech(){
    return this._tech;
  }

  error() {
    return this._error;
  }

  playerPlayInterval() {
    return this._playerPlayInterval;
  }

  playlistChecker() {
    return this._playlistChecker;
  }

  playlistCheckerInterval() {
    return this._playlistCheckerInterval;
  }

  playlists() {
    return this.tech().plugin().hlsObject().hls().playlists;
  }

  _getMediaAttribute() {
    const hls = this.tech().plugin().hlsObject();
    const dash = this.tech().plugin().dashObject();

    if (dash.hasDashObject()) return dash.getCurrentMediaAttribute();
    return hls.getCurrentMediaAttribute();
  }

  getPlaylistStatus() {
    this.tech().on('play', () => {
      this._playlistChecker = setInterval(() => {
        if (this.playlists().error && this.playlists().error.status === 404) {
          this.triggerMissingM3U8();
          this.clearPlaylistChecker();
        }
      }, this.playlistCheckerInterval());
    });

    this.tech().on('loadedmetadata', () => {
      this.clearPlaylistChecker();
    });
  }

  triggerMissingM3U8() {
    this.error().showError(6);
    this.sendMissingM3U8Event();
  }

  clearPlaylistChecker() {
    clearInterval(this._playlistChecker);
    this._playlistChecker = undefined;
  }

  bindPause(){
    this.tech().on("vp-pause", function(e){
      this.tech().trigger({
        type: "vp-video-pause",
        id: e.id,
        play_uuid: this.uuid(),
        genreIds: this.data().genreIds(),
        userTriggered: e.userTriggered,
        eventTime: e.eventTime
      });
    }.bind(this));
  }

  bindResume(){
    this.tech().on("vp-resume", function(e){
      this.tech().trigger({
        type: "vp-video-resume",
        id: e.id,
        play_uuid: this.uuid()
      });
    }.bind(this));
  }

  bindSeek(){
    var position, action = 'seekbar';

    this.tech().on('vp-before-seeked', (e) => {
      action = e.action;
    });

    this.tech().on("timeupdate", function(){
      position = this.tech().currentTime();
    }.bind(this));

    this.tech().on("seeking", function(){
      var offset = this.tech().currentTime() - position;
      this.tech().trigger({
        type: "vp-video-seek",
        id: this.data().videoId(),
        play_uuid: this.uuid(),
        position: position,
        offset: offset,
        action: action
      });
      action = 'seekbar';
    }.bind(this));
  }

  bindMeta(){
    this.tech().on("vp-meta", function(e){
      this.tech().trigger({
        type: "vp-video-meta",
        id: e.id,
        level: e.level,
        levels: e.levels,
        targetBitrate: e.targetBitrate,
        play_uuid: this.uuid(),
        genreIds: this.data().genreIds()
      });
    }.bind(this));
  }

  bindBuffer() {
    this.tech().on('vp-buffer', (e) => {
      this.tech().trigger({
        type: 'vp-video-buffer',
        id: e.id,
        seconds: e.seconds,
        play_uuid: this.uuid(),
        genreIds: this.data().genreIds(),
        position: e.position,
        timer: e.timer,
        embed: e.embed,
        bandwidth: this.tech().getBandwidth(),
        quality: this.tech().getQuality(),
        videoSourceBitrate: e.videoSourceBitrate,
        videoSourceHeight: e.videoSourceHeight,
        videoSourceWidth: e.videoSourceWidth,
        playerHeight: e.playerHeight,
        playerWidth: e.playerWidth,
      });
    });
  }

  bindError(){
    this.tech().on("vp-error", function(e){
      this.tech().trigger({
        type: "vp-video-error",
        id: e.id,
        errorCode: e.errorCode,
        errorMessage: e.errorMessage,
        videoDuration: e.videoDuration,
        currentTime: e.currentTime,
        embed: e.embed,
        play_uuid: this.uuid(),
        readyState: this.tech().readyState(),
        networkState: this.tech().networkState(),
        bufferedVideoFrames: this.tech().bufferedVideoFrames(),
        tokenRefreshCount: this.data().tokenRefreshCount()
      });
    }.bind(this));
  }

  sendMissingM3U8Event() {
    this.tech().trigger({
      type: 'vp-video-error',
      id: this.data().videoId(),
      errorCode: 6,
      errorMessage: 'missing m3u8 playlist',
      videoDuration: 0,
      currentTime: 0,
      embed: this.data().embed(),
      play_uuid: this.data().playUUID(),
      readyState: this.tech().readyState(),
      networkState: this.tech().networkState(),
      bufferedVideoFrames: this.tech().bufferedVideoFrames(),
      tokenRefreshCount: this.data().tokenRefreshCount()
    });
  }

  bindStart(){
    this.tech().on("vp-start", function(e){
      this.tech().trigger({
        type: "vp-video-start",
        id: e.id,
        embed: e.embed,
        videoDuration: e.videoDuration,
        hasAd: e.hasAd,
        autoplay: e.autoplay,
        codec: e.codec,
        play_uuid: this.uuid(),
        genreIds: this.data().genreIds(),
        currentTime: this.tech().currentTime(),
        readyState: this.tech().readyState(),
        networkState: this.tech().networkState(),
        bufferedVideoFrames: this.tech().bufferedVideoFrames(),
        tokenRefreshCount: this.data().tokenRefreshCount(),
        bandwidth: this.tech().getBandwidth(),
        quality: this.tech().getQuality(),
        is_drm: e.is_drm,
        videoSourceBitrate: e.videoSourceBitrate,
        videoSourceHeight: e.videoSourceHeight,
        videoSourceWidth: e.videoSourceWidth,
        playerHeight: e.playerHeight,
        playerWidth: e.playerWidth,
      });
    }.bind(this));
  }

  bindPlay() {
    this.playerPlayInterval().register();
  }

  bindAdError(){
    this.tech().on('vp-adserror', (e) => {
      this.tech().trigger({
        type: 'vp-video-adserror',
        id: e.id,
        adType: e.adType,
        message: e.message,
        error_code: e.error_code,
        videoDuration: e.videoDuration,
        currentTime: e.currentTime,
        adTagUrl: e.adTagUrl,
        embed: e.embed,
        play_uuid: this.uuid(),
        adUUID: e.adUUID
      });
    });
  }

  bindAdWatch(){
    this.tech().on("vp-adwatch", function(e){
      this.tech().trigger({
        type: "vp-video-adwatch",
        id: e.id,
        duration: e.duration,
        currentTime: e.currentTime,
        play_uuid: this.uuid(),
        adUUID: e.adUUID,
        ad_type: e.ad_type
      });
    }.bind(this));
  }

  bindUnmuteButtonLoad(){
    this.tech().on('vp-unmute-button-load-event', function(e){
      this.tech().trigger({
        type          : 'vp-unmute-button-load',
        embed         : this.data().embed(),
        play_uuid     : this.uuid(),
        id            : e.id,
        videoDuration : e.videoDuration,
        hasAd         : e.hasAd,
        autoplay      : e.autoplay
      });
    }.bind(this));
  }

  bindUnmuteButtonClick(){
    this.tech().on('vp-unmute-button-click-event', function(e){
      this.tech().trigger({
        type          : 'vp-unmute-button-click',
        embed         : this.data().embed(),
        play_uuid     : this.uuid(),
        id            : e.id,
        videoDuration : e.videoDuration,
        hasAd         : e.hasAd,
        autoplay      : e.autoplay
      });
    }.bind(this));
  }

  bindTokenRefresh(){
    this.tech().on("vp-token-refresh", function(){
      var properties = {
        action: 'restart_token',
        service: 'vod',
        play_uuid: this.uuid(),
        current_time: this.tech().currentTime(),
        duration: this.data().videoDuration(),
        video_id: this.data().videoId()
      };

      PlayerAhoyCommonEvent.sendEvent('PLAYBACK', properties);
    }.bind(this));
  }

  bindDownloadAppButtonClick() {
    this.tech().on("vp-download-app-button-click", () => {
      const properties = {
        action: 'download app',
        section: 'video player'
      };

      PlayerAhoyCommonEvent.sendEvent('CTA', properties, 'VIDEO::');
    });
  }

  bindHrefButtonClick() {
    this.tech().on("vp-href-button-click", () => {
      const properties = {
        action: 'link vidio',
        section: 'video player'
      };

      PlayerAhoyCommonEvent.sendEvent('CTA', properties, 'VIDEO::');
    });
  }

  bindReportButtonClick() {
    this.tech().on("vp-report-button-click", () => {
      const properties = {
        action: 'send report',
        section: 'video player'
      };

      PlayerAhoyCommonEvent.sendEvent('CTA', properties, 'VIDEO::');
    });
  }

  bindFeedbackButtonClick() {
    this.tech().on("vp-feedback-button-click", () => {
      const properties = {
        action: 'send feedback',
        section: 'video player'
      };

      PlayerAhoyCommonEvent.sendEvent('CTA', properties, 'VIDEO::');
    });
  }

  bindRetryButtonClick() {
    this.tech().on("vp-retry-button-click", () => {
      const properties = {
        action: 'retry again',
        section: 'video player'
      };

      PlayerAhoyCommonEvent.sendEvent('CTA', properties, 'VIDEO::');
    });
  }
}
